import { formatTimestamp } from '@/lib/protobuf-utils'
import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Modal } from '@magnetic/modal'
import { Select } from '@magnetic/select'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Spinner } from '@magnetic/spinner'
import { Text } from '@magnetic/text'
import { Breadcrumb } from '@magnetic/breadcrumb'
import { Tooltip } from '@magnetic/tooltip'
import { Tag } from '@magnetic/tag'
import { Tabs } from '@magnetic/tabs'
import { Skeleton } from '@magnetic/skeleton'
import { MembersTable } from '@/components/members-table'
import { Row, Col } from '@magnetic/grid'
import { Checkbox } from '@magnetic/switch'
import { Container } from '@magnetic/container'
import { KeyValue } from '@magnetic/key-value'
import { useToast } from '@magnetic/notification'
import { useAuth } from '@/lib/use-auth'
import { useModalStore, useTabStore } from '@/stores'
import { deriveSyncHealthFromState } from '@/utils/applicationStatus'
import { useProjectDetailActions } from '@/hooks/useProjectDetailActions'
import type { Application as ProtoApplication, Project as ProtoProject, ProjectMember } from '@/lib/generated-types'
import {
  ArrowLeft,
  Plus,
  Cube,
  WarningCircle,
  ArrowsClockwise,
  UserPlus,
  Trash,
  ArrowSquareOut,
  Play,
  Power,
  Warning,
  // Magnetic Status Icons
  Positive,
  WarningStatus,
  Information,
} from '@/lib/icons'
import {
  getProject,
  listApplications,
  listProjectMembers,
} from '@/lib/api'

// Helper function to get user's role on a project
type RoleProject = Pick<ProtoProject, 'members' | 'owners'>
type CardApplication = Pick<ProtoApplication, 'state' | 'stateMessage' | 'deletedAt'>

function getUserRoleOnProject(project: RoleProject, userEmail: string): 'admin' | 'editor' | 'viewer' | null {
  // Check if user is an owner first - owners are treated as admins
  if (project?.owners && project.owners.includes(userEmail)) {
    return 'admin'
  }

  // Check role from members list
  if (!project?.members) return null
  const member = project.members.find((m) => m.userId === userEmail)
  return member?.role as 'admin' | 'editor' | 'viewer' | null
}

export function ProjectDetailPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { openModal, closeModal, isOpen } = useModalStore()
  const { setTab, getTab } = useTabStore()

  const [formData, setFormData] = useState({
    displayName: '',
    path: '',
    targetRevision: 'main',
    autoSync: true,
    selfHeal: true,
    pruneResources: true,
  })
  const [memberFormData, setMemberFormData] = useState({
    type: 'user' as 'user' | 'group',
    identifier: '',
    role: 'viewer'
  })
  const activeTab = getTab(`project-${projectId}`, 'applications')

  const { addToast } = useToast()

  const { data: projectResponse, isLoading: projectLoading, error: projectError } = useConnectQuery(getProject, { id: projectId! }, {
    enabled: !!projectId,
  })
  const project = projectResponse?.project

  // Get user's role on this project
  const userRole = useMemo(() => {
    if (!project || !user) return null
    return getUserRoleOnProject(project, user.email)
  }, [project, user])

  // Check if user is owner - owners are admins
  const isOwner = useMemo(() => {
    if (!project?.owners || !user) return false
    return project.owners.includes(user.email)
  }, [project, user])

  const isAdmin = userRole === 'admin' || isOwner
  const canEdit = userRole === 'admin' || userRole === 'editor' || isOwner

  const { data: applicationsResponse, isLoading: appsLoading } = useConnectQuery(listApplications, { projectId: projectId! }, {
    enabled: !!projectId,
    refetchInterval: 30000,
  })
  const applications = applicationsResponse?.applications || []

  const {
    createApplicationMutation,
    retryCreateCRDMutation,
    retrySetupMutation,
    syncApplicationMutation,
    suspendApplicationMutation,
    resumeApplicationMutation,
    addMemberMutation,
    removeMemberMutation,
  } = useProjectDetailActions({
    onCreateApplicationSuccess: (appId) => {
      closeModal()
      setFormData({
        displayName: '',
        path: '',
        targetRevision: 'main',
        autoSync: true,
        selfHeal: true,
        pruneResources: true,
      })
      if (appId) {
        navigate(`/projects/${projectId}/applications/${appId}`)
      }
    },
    onAddMemberSuccess: () => {
      closeModal()
      setMemberFormData({ type: 'user', identifier: '', role: 'viewer' })
    },
  })

  const isDeleting = (app: CardApplication) => !!app.deletedAt
  const hasCRDError = (app: CardApplication) => app.state === 'CRDCreationFailed'
  const hasSetupError = (app: CardApplication) => app.state === 'RepoValidationFailed' || app.state === 'AWSProvisioningFailed'
  const visibleApplications = applications.filter((app) => !isDeleting(app))

  const getIngressHost = (ingressUrl?: string) => {
    if (!ingressUrl) return ''
    try {
      return new URL(ingressUrl).host
    } catch {
      return ingressUrl.replace(/^https?:\/\//, '')
    }
  }

  const getIngressStatus = (app: ProtoApplication) => {
    if (!app.ingressUrl) return { label: 'IngressRoute missing', color: 'var(--mds-color-theme-text-warning-normal)' }
    if (app.ingressReady) return { label: 'IngressRoute ready', color: 'var(--mds-color-theme-text-positive-normal)' }
    return { label: 'IngressRoute pending', color: 'var(--mds-color-theme-text-warning-normal)' }
  }

  const handleNewApplication = () => {
    openModal('create-application')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.displayName.trim() || !formData.path.trim()) {
      addToast({
        status: 'warning',
        title: 'Validation error',
        children: 'Display name and path are required',
      })
      return
    }
    if (!project?.repoUrl) {
      addToast({
        status: 'warning',
        title: 'Validation error',
        children: 'Project repository URL is missing',
      })
      return
    }

    // Always create the application - validation errors will be shown in the UI
    // and users can fix them later with retry buttons
    createApplicationMutation.mutate({
      projectId: projectId!,
      displayName: formData.displayName,
      path: formData.path,
      syncPolicy: {
        autoSync: formData.autoSync,
        selfHeal: formData.selfHeal,
        pruneResources: formData.pruneResources,
      },
    })
  }

  const handleRetryCRD = (appId: string) => {
    retryCreateCRDMutation.mutate({ id: appId })
  }

  const handleRetrySetup = (appId: string) => {
    retrySetupMutation.mutate({ id: appId })
  }

  const handleAddMember = () => {
    setMemberFormData({ type: 'user', identifier: '', role: 'viewer' })
    openModal('add-member')
  }

  const handleMemberSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!memberFormData.identifier.trim()) {
      addToast({
        status: 'warning',
        title: 'Validation error',
        children: memberFormData.type === 'user' ? 'Email is required' : 'Group ID is required',
      })
      return
    }

    addMemberMutation.mutate({
      projectId: projectId!,
      ...(memberFormData.type === 'user'
        ? { userId: memberFormData.identifier }
        : { groupId: memberFormData.identifier }
      ),
      role: memberFormData.role
    })
  }

  const handleRemoveMember = (member: ProjectMember) => {
    if (!confirm(`Remove ${member.userId ? 'user' : 'group'} from project?`)) {
      return
    }

    removeMemberMutation.mutate({
      projectId: projectId!,
      userId: member.userId,
      groupId: member.groupId
    })
  }

  if (projectLoading || appsLoading) {
    return (
      <Flex direction="vertical" gap="lg">
        {/* Breadcrumb skeleton */}
        <Skeleton height="sm" style={{ width: 200 }} />

        {/* Header skeleton */}
        <Flex direction="vertical" gap="xs">
          <Skeleton height="lg" shade="dark" style={{ width: 300 }} />
          <Skeleton height="sm" style={{ width: 400 }} />
        </Flex>

        {/* Tabs skeleton */}
        <Flex direction="horizontal" gap={16}>
          <Skeleton height="md" style={{ width: 100 }} />
          <Skeleton height="md" style={{ width: 100 }} />
          <Skeleton height="md" style={{ width: 100 }} />
        </Flex>

        {/* Content skeleton - cards */}
        <Container>
          <Card.Group>
            {[...Array(3)].map((_, i) => (
              <Card key={i} style={{ minHeight: 200 }}>
                <Flex direction="vertical" gap={16}>
                  <Skeleton height="md" shade="dark" style={{ width: '60%' }} />
                  <Skeleton height="sm" lines={3} oblique />
                </Flex>
              </Card>
            ))}
          </Card.Group>
        </Container>
      </Flex>
    )
  }

  if (projectError || !project) {
    return (
      <Flex direction="vertical" align="center" gap="md" style={{ padding: 48 }}>
        <WarningCircle size={48} style={{ marginBottom: 16 }} />
        <Heading size="sub-section">Failed to load project</Heading>
        <Text size="sm" color="light">
          {projectError instanceof Error ? projectError.message : 'Project not found'}
        </Text>
        <Link to="/projects">
          <Button kind="secondary" icon={<ArrowLeft size={16} />}>
            Back to Projects
          </Button>
        </Link>
      </Flex>
    )
  }

  const getSyncIcon = (syncStatus: string) => {
    switch (syncStatus) {
      case 'Synced':
        return <Positive />
      case 'OutOfSync':
        return <WarningStatus />
      default:
        return <Information />
    }
  }

  return (
    <Flex direction="vertical" gap="lg">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Link href="/projects">Projects</Breadcrumb.Link>
        <Breadcrumb.Link isLast>{project.name}</Breadcrumb.Link>
      </Breadcrumb>

      {/* Create Application Modal */}
      <Modal isOpen={isOpen('create-application')} onClose={closeModal} size="md">
        <Modal.Content>
          <form onSubmit={handleSubmit}>
            <Flex direction="vertical" gap="lg" style={{ padding: 24 }}>
              <Flex direction="vertical" gap="sm">
                <Heading size="section">Create New Application</Heading>
                <Text size="p4" color="light">Add a new application to this project. Each application has one ECR repository (one container image). For multiple images, create multiple applications.</Text>
              </Flex>

              <Flex direction="vertical" gap="md">
                <Input
                  label="Display Name *"
                  placeholder="My Application"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  hint="A friendly name for your application. A slug will be auto-generated."
                />

                <Input
                  label="Repository URL"
                  value={project?.repoUrl || ''}
                  disabled
                  hint="All applications in this project use the same repository"
                />

                <Input
                  label="Path *"
                  placeholder="helm/app-name"
                  value={formData.path}
                  onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                  hint="Path to your Helm chart in the repository (e.g., helm/app-name)"
                />

                <Input
                  label="Target Revision"
                  placeholder="main"
                  value={formData.targetRevision}
                  onChange={(e) => setFormData({ ...formData, targetRevision: e.target.value })}
                />

                <Flex direction="vertical" gap="sm">
                  <Checkbox
                    checked={formData.autoSync}
                    onClick={() => setFormData({ ...formData, autoSync: !formData.autoSync })}
                  >
                    Enable Auto-Sync
                  </Checkbox>
                  <Checkbox
                    checked={formData.selfHeal}
                    onClick={() => setFormData({ ...formData, selfHeal: !formData.selfHeal })}
                  >
                    Enable Self-Heal
                  </Checkbox>
                  <Checkbox
                    checked={formData.pruneResources}
                    onClick={() => setFormData({ ...formData, pruneResources: !formData.pruneResources })}
                  >
                    Prune Resources
                  </Checkbox>
                </Flex>
              </Flex>

              <Flex justify="flex-end" gap="sm">
                <Button
                  kind="secondary"
                  onClick={closeModal}
                  disabled={createApplicationMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createApplicationMutation.isPending}
                  icon={createApplicationMutation.isPending
                    ? <Spinner size="sm" />
                    : <Plus size={16} />
                  }
                >
                  {createApplicationMutation.isPending ? 'Creating...' : 'Create Application'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Modal.Content>
      </Modal>

      {/* Project Overview Container */}
      <Container>
        <Flex direction="vertical" gap="lg">
          {/* Project Header */}
          <Flex direction="vertical" gap="xs">
            <Heading size="primary">{project.name}</Heading>
            <Text size="p4" color="light">{project.description || 'No description'}</Text>
          </Flex>

          {/* Project Details and Links */}
          <Row gap={["m", "m"]} align="stretch">
            {/* Project Details Card */}
            <Col span={4} sm={12} md={6} lg={6}>
              <Card>
                <Flex direction="vertical" gap="sm">
                  <Heading size="sub-section">Project Details</Heading>
                  <KeyValue size="sm">
                    <KeyValue.Pair
                      pair={[
                        "Repository",
                        <a
                          href={project.repoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--mds-color-theme-text-accent-normal)' }}
                        >
                          {project.repoUrl?.replace('https://github.com/', '') || project.repoUrl}
                        </a>
                      ]}
                    />
                    <KeyValue.Pair pair={["Namespace", project.namespace]} />
                    <KeyValue.Pair pair={["Owners", project.owners?.join(', ') || 'No owners']} />
                    <KeyValue.Pair pair={["Applications", applications.length]} />
                    {project.createdAt && (
                      <KeyValue.Pair pair={["Created", formatTimestamp(project.createdAt)]} />
                    )}
                  </KeyValue>
                </Flex>
              </Card>
            </Col>

            {/* Application Links */}
            <Col span={4} sm={12} md={6} lg={6}>
              <Card>
                <Flex direction="vertical" gap="sm">
                  <Heading size="sub-section">Application Links</Heading>
                  {visibleApplications.length > 0 ? (
                    <Flex direction="vertical" gap="sm">
                      {visibleApplications.map((app) => {
                        const ingressHost = getIngressHost(app.ingressUrl)
                        const ingressStatus = getIngressStatus(app)

                        return (
                          <Flex
                            key={app.id}
                            direction="vertical"
                            gap="xs"
                            style={{
                              padding: '8px 0',
                              borderBottom: '1px solid var(--mds-color-theme-border-subtle)',
                            }}
                          >
                            <Flex align="center" justify="space-between" gap="sm">
                              <Text size="p4">
                                {ingressHost || `${app.displayName || app.name} (no host yet)`}
                              </Text>
                              {app.ingressUrl && (
                                <a
                                  href={app.ingressUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Open ${app.displayName || app.name} in new tab`}
                                >
                                  <ArrowSquareOut size={16} />
                                </a>
                              )}
                            </Flex>
                            <Link
                              to={`/projects/${projectId}/applications/${app.id}`}
                              style={{
                                color: ingressStatus.color,
                                textDecoration: 'underline',
                                fontSize: '12px',
                              }}
                            >
                              {ingressStatus.label}
                            </Link>
                          </Flex>
                        )
                      })}
                    </Flex>
                  ) : (
                    <Text size="p4" color="light">No active applications</Text>
                  )}
                </Flex>
              </Card>
            </Col>
          </Row>
        </Flex>
      </Container>

      {/* Tabs and Content Container */}
      <Container>
        <Flex direction="vertical" gap="md">
          {/* Tabs Navigation */}
          <Tabs>
            <Tabs.Link
              href="#applications"
              selected={activeTab === 'applications'}
              onClick={(e) => {
                e.preventDefault()
                setTab(`project-${projectId}`, 'applications')
              }}
            >
              {`Applications (${applications.length})`}
            </Tabs.Link>
            {/* Permissions tab - visible to admins and owners */}
            {isAdmin && (
              <Tabs.Link
                href="#permissions"
                selected={activeTab === 'permissions'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(`project-${projectId}`, 'permissions')
                }}
              >
                Permissions
              </Tabs.Link>
            )}
            {/* Settings tab - visible to admins, editors, and owners */}
            {canEdit && (
              <Tabs.Link
                href="#settings"
                selected={activeTab === 'settings'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(`project-${projectId}`, 'settings')
                }}
              >
                Settings
              </Tabs.Link>
            )}
          </Tabs>

          {/* Settings Tab */}
          {activeTab === 'settings' && canEdit && (
            <Card style={{ boxShadow: 'none' }}>
              <Flex direction="vertical" gap="lg" style={{ padding: 24 }}>
                {/* Project Configuration Section */}
                <Flex direction="vertical" gap="md">
                  <Heading size="sub-section">Project Configuration</Heading>

                  <KeyValue>
                    <KeyValue.Pair pair={["Display Name", project?.displayName || project?.name]} />
                    <KeyValue.Pair pair={["Namespace", project?.namespace]} />
                    <KeyValue.Pair pair={["Repository", project?.repoUrl]} />
                    <KeyValue.Pair
                      pair={[
                        "Tags",
                        project?.tags && project.tags.length > 0 ? (
                          <Flex gap={8} wrap>
                            {project.tags.map((tag: string) => (
                              <Tag key={tag} color="lavender">{tag}</Tag>
                            ))}
                          </Flex>
                        ) : (
                          <Text color="tertiary">No tags</Text>
                        )
                      ]}
                    />
                  </KeyValue>
                </Flex>

                {/* Resource Quotas Section */}
                {project?.resourceQuota && (
                  <Flex direction="vertical" gap="md">
                    <Heading size="sub-section">Resource Quotas</Heading>

                    <KeyValue>
                      <KeyValue.Pair pair={["CPU Limit", project.resourceQuota.cpuLimit || 'Not set']} />
                      <KeyValue.Pair pair={["Memory Limit", project.resourceQuota.memoryLimit || 'Not set']} />
                      <KeyValue.Pair pair={["CPU Request", project.resourceQuota.cpuRequest || 'Not set']} />
                      <KeyValue.Pair pair={["Memory Request", project.resourceQuota.memoryRequest || 'Not set']} />
                    </KeyValue>
                  </Flex>
                )}

                {/* Danger Zone - Admins and owners only */}
                {isAdmin && (
                  <Flex direction="vertical" gap="md" style={{
                    padding: 16,
                    border: '1px solid var(--color-border-negative)',
                    borderRadius: 8,
                    backgroundColor: 'var(--mds-color-theme-background-negative-subtle)',
                  }}>
                    <Flex direction="vertical" gap="xs">
                      <Flex gap={8} align="center">
                        <Warning size={20} color="var(--color-text-negative)" />
                        <Heading size="sub-section" style={{ color: 'var(--color-text-negative)' }}>
                          Danger Zone
                        </Heading>
                      </Flex>
                      <Text size="p4" color="secondary">
                        Irreversible and destructive actions
                      </Text>
                    </Flex>

                    <Flex direction="vertical" gap="sm">
                      <Flex justify="space-between" align="center" style={{
                        padding: 12,
                        border: '1px solid var(--color-border-weak)',
                        borderRadius: 4,
                        backgroundColor: 'var(--color-background-primary)',
                      }}>
                        <Flex direction="vertical" gap="xs">
                          <Text weight="semibold">Delete Project</Text>
                          <Text size="p4" color="secondary">
                            Permanently delete this project and all its applications. This action cannot be undone.
                          </Text>
                        </Flex>
                        <Button
                          kind="primary"
                          destructive
                          size="sm"
                          icon={<Trash weight="bold" size={16} />}
                          onClick={() => openModal('delete-confirm')}
                        >
                          Delete Project
                        </Button>
                      </Flex>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Card>
          )}

          {/* Permissions Tab - Admins and owners only */}
          {activeTab === 'permissions' && isAdmin && (
            <MembersTable
              projectId={projectId!}
              queryFn={listProjectMembers}
              title="Project Members"
              description="Manage who has access to this project"
              selectableRows={false}
              configComponents={
                <Button size="sm" onClick={handleAddMember} icon={<UserPlus size={16} />}>
                  Add Member
                </Button>
              }
              renderActions={(member) => (
                <Button
                  kind="tertiary"
                  size="sm"
                  onClick={() => handleRemoveMember(member)}
                  disabled={removeMemberMutation.isPending}
                  icon={<Trash size={16} />}
                />
              )}
            />
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <Flex direction="vertical" gap="md">
              <Flex align="center" justify="flex-end">
                <Button onClick={handleNewApplication} icon={<Plus size={16} />}>
                  New Application
                </Button>
              </Flex>

              {applications.length > 0 ? (
                <Row gap={["m", "m"]} align="stretch">
                  {applications.map((app) => {
                    const appIsDeleting = isDeleting(app)
                    const appHasCRDError = hasCRDError(app)
                    const appHasSetupError = hasSetupError(app)
                    const appHasEcrError = app.imageDeploymentStatus === 'failed'
                    const { syncStatus } = deriveSyncHealthFromState(app.state)
                    const isSuspended = app.state === 'Suspended'

                    return (
                      <Col key={app.id} span={4} sm={12} md={6} lg={4}>
                        <Card
                          accent={appIsDeleting ? 'warning' : (appHasCRDError || appHasSetupError) ? 'negative' : undefined}
                          onClick={!appIsDeleting
                            ? () => navigate(`/projects/${projectId}/applications/${app.id}`)
                            : undefined
                          }
                          onKeyDown={(event) => {
                            if (appIsDeleting) {
                              return
                            }
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              navigate(`/projects/${projectId}/applications/${app.id}`)
                            }
                          }}
                          style={{
                            opacity: appIsDeleting ? 0.6 : 1,
                            height: '100%',
                            ...(appIsDeleting ? { boxShadow: 'none' } : {}),
                          }}
                        >
                          <Flex direction="vertical" gap="md" style={{ padding: 16, height: '100%' }}>
                            <Flex align="center" justify="space-between" gap="sm">
                              <Flex direction="vertical" style={{ flex: 1, minWidth: 0 }}>
                                <Heading size="sub-section">{app.displayName || app.name}</Heading>
                              </Flex>
                              <Flex gap="sm" align="center">
                                <Tooltip
                                  title={syncApplicationMutation.isPending ? 'Syncing' : 'Sync application'}
                                  content={syncApplicationMutation.isPending ? 'Syncing' : 'Sync application'}
                                  placement="bottom"
                                >
                                  <Button
                                    kind="tertiary"
                                    size="sm"
                                    icon={syncApplicationMutation.isPending
                                      ? <Spinner size="sm" />
                                      : <ArrowsClockwise size={16} weight="bold" />
                                    }
                                    disabled={appIsDeleting || syncApplicationMutation.isPending}
                                    aria-label={syncApplicationMutation.isPending ? 'Syncing application' : 'Sync application'}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      syncApplicationMutation.mutate({ id: app.id })
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip
                                  title={isSuspended
                                    ? (resumeApplicationMutation.isPending ? 'Enabling application' : 'Enable application')
                                    : (suspendApplicationMutation.isPending ? 'Disabling application' : 'Disable application')
                                  }
                                  content={isSuspended
                                    ? (resumeApplicationMutation.isPending ? 'Enabling application' : 'Enable application')
                                    : (suspendApplicationMutation.isPending ? 'Disabling application' : 'Disable application')
                                  }
                                  placement="bottom"
                                >
                                  <Button
                                    kind="tertiary"
                                    size="sm"
                                    icon={isSuspended
                                      ? <Play size={16} weight="bold" />
                                      : <Power size={16} weight="bold" />
                                    }
                                    disabled={appIsDeleting || suspendApplicationMutation.isPending || resumeApplicationMutation.isPending}
                                    aria-label={isSuspended ? 'Enable application' : 'Disable application'}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (isSuspended) {
                                        resumeApplicationMutation.mutate({ id: app.id })
                                      } else {
                                        suspendApplicationMutation.mutate({ id: app.id })
                                      }
                                    }}
                                  />
                                </Tooltip>
                              </Flex>
                            </Flex>

                            <Text size="p4" color="light" style={{ wordBreak: 'break-word' }}>
                              {appIsDeleting
                                ? 'Deleting...'
                                : appHasCRDError
                                  ? `CRD Error: ${app.stateMessage || 'CRD creation failed'}`
                                  : appHasSetupError
                                    ? `Setup Error: ${app.stateMessage || 'Repository/AWS setup failed'}`
                                  : (app.stateMessage || 'No description available')
                              }
                            </Text>

                            {/* CRD Error with retry button */}
                            {appHasCRDError && (
                              <Flex align="center" gap="sm" style={{ marginTop: 8 }}>
                                <Button
                                  kind="secondary"
                                  size="sm"
                                  icon={retryCreateCRDMutation.isPending ? <Spinner size="sm" /> : <ArrowsClockwise size={16} />}
                                  disabled={retryCreateCRDMutation.isPending}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleRetryCRD(app.id)
                                  }}
                                >
                                  {retryCreateCRDMutation.isPending ? 'Retrying...' : 'Retry CRD Creation'}
                                </Button>
                              </Flex>
                            )}

                            {appHasSetupError && (
                              <Flex align="center" gap="sm" style={{ marginTop: 8 }}>
                                <Button
                                  kind="secondary"
                                  size="sm"
                                  icon={retrySetupMutation.isPending ? <Spinner size="sm" /> : <ArrowsClockwise size={16} />}
                                  disabled={retrySetupMutation.isPending}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleRetrySetup(app.id)
                                  }}
                                >
                                  {retrySetupMutation.isPending ? 'Retrying...' : 'Retry Setup'}
                                </Button>
                              </Flex>
                            )}

                            {appHasEcrError && (
                              <Flex align="center" gap="xs" style={{ color: 'var(--mds-color-theme-text-error-normal)' }}>
                                <WarningCircle size={16} />
                                <Text size="p4">
                                  {app.imageDeploymentMessage || 'ECR deployment failed. Please retry or contact support.'}
                                </Text>
                              </Flex>
                            )}

                            <Card.Divider />

                            {!appIsDeleting && !appHasCRDError && !appHasSetupError && (
                              <KeyValue>
                                <KeyValue.Pair
                                  pair={[
                                    <Flex align="center" gap={8}>
                                      {getSyncIcon(syncStatus)}
                                      <Text size="p3" weight="bold">{syncStatus}</Text>
                                    </Flex>,
                                    app.lastSyncTime
                                      ? `Last sync: ${formatTimestamp(app.lastSyncTime)}`
                                      : 'Last sync: N/A',
                                  ]}
                                />
                                <KeyValue.Pair
                                  pair={[
                                    'Path',
                                    app.path || 'N/A',
                                  ]}
                                />
                              </KeyValue>
                            )}

                            {/* CRD Error badge */}
                            {appHasCRDError && (
                              <Tag.Status status="negative">CRD Failed</Tag.Status>
                            )}

                            {appHasSetupError && (
                              <Tag.Status status="negative">Setup Failed</Tag.Status>
                            )}

                            {/* Deleting badge */}
                            {appIsDeleting && (
                              <Tag.Status status="warning">Deleting</Tag.Status>
                            )}

                          </Flex>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>
              ) : (
                <Card style={{ boxShadow: 'none' }}>
                  <Flex direction="vertical" align="center" gap="md" style={{ padding: 48 }}>
                    <Cube size={48} style={{ opacity: 0.5 }} />
                    <Heading size="sub-section">No applications yet</Heading>
                    <Text size="p4" color="light">Create your first application to get started</Text>
                  </Flex>
                </Card>
              )}
            </Flex>
          )}
        </Flex>
      </Container>

      {/* Add Member Modal */}
      <Modal isOpen={isOpen('add-member')} onClose={closeModal} size="sm">
        <Modal.Content>
          <form onSubmit={handleMemberSubmit}>
            <Flex direction="vertical" gap="lg" style={{ padding: 24 }}>
              <Flex direction="vertical" gap="sm">
                <Heading size="section">Add Project Member</Heading>
                <Text size="p4" color="light">
                  Add a user or group to this project with specific permissions
                </Text>
              </Flex>

              <Flex direction="vertical" gap="md">
                <Flex direction="vertical" gap="xs">
                  <Text size="p3" weight="bold">Member Type</Text>
                  <Select
                    value={memberFormData.type}
                    onChange={(value) => setMemberFormData({ ...memberFormData, type: value as 'user' | 'group', identifier: '' })}
                    options={[
                      { value: 'user', label: 'User (by email)' },
                      { value: 'group', label: 'OIDC Group' },
                    ]}
                  />
                </Flex>

                <Input
                  label={memberFormData.type === 'user' ? 'Email Address' : 'Group ID'}
                  placeholder={memberFormData.type === 'user' ? 'user@cisco.com' : 'gde-cat-developers'}
                  value={memberFormData.identifier}
                  onChange={(e) => setMemberFormData({ ...memberFormData, identifier: e.target.value })}
                />

                <Flex direction="vertical" gap="xs">
                  <Text size="p3" weight="bold">Role</Text>
                  <Select
                    value={memberFormData.role}
                    onChange={(value) => setMemberFormData({ ...memberFormData, role: value as string })}
                    options={[
                      { value: 'viewer', label: 'Viewer - Read-only access' },
                      { value: 'editor', label: 'Editor - Can deploy applications' },
                      { value: 'admin', label: 'Admin - Can manage applications' },
                    ]}
                  />
                </Flex>
              </Flex>

              <Flex justify="flex-end" gap="sm">
                <Button
                  kind="secondary"
                  onClick={closeModal}
                  disabled={addMemberMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addMemberMutation.isPending}
                  icon={addMemberMutation.isPending
                    ? <Spinner size="sm" />
                    : <UserPlus size={16} />
                  }
                >
                  {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Modal.Content>
      </Modal>

      {/* Delete Project Confirmation Modal */}
      <Modal isOpen={isOpen('delete-confirm')} onClose={closeModal} size="sm">
        <Modal.Content>
          <Flex direction="vertical" gap={16} style={{ padding: 24 }}>
            <Flex direction="horizontal" gap={12} align="center">
              <Warning size={24} weight="bold" color="var(--color-negative)" />
              <Heading size="section">Delete Project</Heading>
            </Flex>

            <Text color="secondary">
              Are you sure you want to delete <Text weight="semibold" style={{ display: 'inline' }}>{project?.displayName || project?.name}</Text>?
            </Text>

            <Flex direction="vertical" gap={8} style={{
              padding: 12,
              backgroundColor: 'var(--mds-color-theme-background-negative-subtle)',
              borderRadius: 8,
            }}>
              <Text size="p4" weight="semibold" style={{ color: 'var(--color-text-negative)' }}>
                This action will:
              </Text>
              <Text size="p4" color="secondary">• Delete all {applications.length} application(s) in this project</Text>
              <Text size="p4" color="secondary">• Remove all ArgoCD resources</Text>
              <Text size="p4" color="secondary">• Delete the Kubernetes namespace</Text>
              <Text size="p4" weight="semibold" style={{ color: 'var(--color-text-negative)', marginTop: 8 }}>
                This cannot be undone.
              </Text>
            </Flex>

            <Flex direction="horizontal" justify="flex-end" gap={12}>
              <Button
                kind="tertiary"
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                kind="primary"
                destructive
                icon={<Trash weight="bold" />}
                onClick={() => {
                  addToast({
                    status: 'information',
                    title: 'Not implemented yet',
                    children: 'Project deletion requires backend implementation. See BACKEND_REQUIREMENTS.md for details.',
                  })
                  closeModal()
                }}
              >
                Delete Project
              </Button>
            </Flex>
          </Flex>
        </Modal.Content>
      </Modal>
    </Flex >
  )
}
